import { useCallback, useEffect, useState } from "react";
import { errorColor, successColor, useAuth } from "../../sdk";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useWarning } from "../../Provider/context";

const getPayload = (value, index, isResavenue = false) => {
  const resavenueFields = {
    isBaseRatePlan: value?.isBaseRatePlan || false,
    roomRatePlanName: value?.roomRatePlanName,
    addToDoubleOccupancy: value?.addToDoubleOccupancy
      ? Number(value?.addToDoubleOccupancy)
      : null,
    addToTripleOccupancy: value?.addToTripleOccupancy
      ? Number(value?.addToTripleOccupancy)
      : null,
    addToQuadOccupancy: value?.addToQuadOccupancy
      ? Number(value?.addToQuadOccupancy)
      : null,
  };

  return {
    propertyId: value?.propertyId,
    roomId: value?.roomId,
    roomRateGroupCode: value?.roomRateGroupCode,
    discount: value?.discount ? Number(value?.discount) : null,
    promotionalDiscount: value?.promotionalDiscount
      ? Number(value?.promotionalDiscount)
      : null,
    roomName: value?.roomName,
    isBaseRoom: value?.isBaseRoom || false,
    addToBasePriceHigh: value?.addToBasePriceHigh
      ? Number(value?.addToBasePriceHigh)
      : null,
    addToBasePriceLow: value?.addToBasePriceLow
      ? Number(value?.addToBasePriceLow)
      : null,
    maxThresholdWeekday: value?.maxThresholdWeekday
      ? Number(value?.maxThresholdWeekday)
      : null,
    minThresholdWeekday: value?.minThresholdWeekday
      ? Number(value?.minThresholdWeekday)
      : null,
    maxThresholdWeekend: value?.maxThresholdWeekend
      ? Number(value?.maxThresholdWeekend)
      : null,
    minThresholdWeekend: value?.minThresholdWeekend
      ? Number(value?.minThresholdWeekend)
      : null,
    sequence: index + 1,
    ...(isResavenue ? resavenueFields : {}),
  };
};

const checkBoxFields = [
  {
    key: "autoPushRatesToChannelManager",
    label: "Auto Push Rates",
  },
  {
    key: "channelManagerAutoSyncEnabled",
    label: "Auto Sync Inventory",
  },
];

const NON_BOOLEAN_CHECKBOX_FIELDS = [
  "noOfDaysChannelManagerForPush",
  "highDemandLevels",
];

export default function useChannelManager() {
  const { authFetch, currentHotel, getHotelDetails } = useAuth();
  const { hotelId } = useParams();
  const { formEdit, setformEdit, handleRedirect } = useWarning();
  const [currentHotelData, setCurrentHotelData] = useState({});
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [excludeRoomToggle, setExcludeRoomToggle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderRoomIndex, setLoaderRoomIndex] = useState(null);
  const [propertyId, setPropertyId] = useState("");
  const [roomData, setRoomData] = useState(null);
  const [roomsData, setRoomsData] = useState([]);
  const [channelManagerData, setChannelManagerData] = useState({
    RoomRatePlanId: null,
    roomId: "",
    roomName: "",
    roomRateGroupCode: "",
    discount: 0,
    enablePropertyConnection: false,
    enablePushData: false,
    sequence: null,
  });
  const [isCheckBoxEdited, setIsCheckBoxEdited] = useState(false);
  const [snackbarMsg, setSnackBarMsg] = useState({
    open: false,
    msg: "",
    color: "",
  });
  const [arrData, setArrData] = useState(null);
  const [openPropertyIdModal, setOpenPropertyIdModal] = useState(false);
  const [demandLevels, setDemandLevels] = useState([]);

  const getDemandLevels = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/city-demand-level`,
      });

      const { data, response } = await get();
      if (data && response.status === 200) {
        setDemandLevels(data);
      }
    } catch (error) {
      console.error("Failed to fetch demand levels:", error);
    }
  }, [authFetch, hotelId]);

  useEffect(() => {
    setPropertyId(currentHotel?.channelManagerPropertyID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHotel?.channelManagerPropertyID]);

  const getAllRooms = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/get-room-rate-plan`,
      });
      const { data, response } = await get();
      if (data && response.status === 200) {
        setChannelManagerData({
          RoomRatePlanId: data[data?.length - 1]?.RoomRatePlanId,
          roomId: data[data?.length - 1]?.roomId,
          roomRateGroupCode: data[data?.length - 1]?.roomRateGroupCode,
          discount: data[data?.length - 1]?.discount,
          enablePropertyConnection:
            data[data?.length - 1]?.enablePropertyConnection,
          enablePushData: data[data?.length - 1]?.enablePushData,
          sequence: data[data?.length - 1]?.sequence,
        });
        setRoomsData(data);
      }
    } catch (err) {
      console.log(err);
    }
  }, [authFetch, hotelId]);

  useEffect(() => {
    getAllRooms();
    getDemandLevels();
  }, [getAllRooms, getDemandLevels]);

  const reorderRoomMappingApi = useCallback(
    async (activeId, formerIdx, laterIdx) => {
      try {
        const { post } = await authFetch({
          path: `/hotel/${hotelId}/room-rate-plan/sequence`,
        });

        const { response, error } = await post({
          formerId: activeId,
          formerSequence: formerIdx,
          laterSequence: laterIdx,
        });

        if (response.status === 204) {
          getAllRooms();
        } else {
          throw new Error(error?.messageToUser || "Failed to reorder rooms.");
        }
      } catch (err) {
        console.log(err);
      }
    },
    [authFetch, getAllRooms, hotelId]
  );

  const getRoomMapping = useCallback(async () => {
    try {
      setIsLoading(true);
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/hotel-room-mapping`,
      });
      const { data, response } = await get();
      if (data && response.status === 200) {
        if (data?.mappings) {
          setRoomData(data?.mappings);
        } else {
          setRoomData(data);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, [authFetch, hotelId]);

  useEffect(() => {
    if (currentHotel) {
      setCurrentHotelData(currentHotel);
    }
  }, [currentHotel]);

  const handleSavePropertyId = useCallback(async () => {
    try {
      const requestBody = {
        propertyId: propertyId,
        roomId: channelManagerData.roomId,
        roomRateGroupCode: channelManagerData?.roomRateGroupCode,
        discount: channelManagerData?.discount,
        enablePropertyConnection: channelManagerData?.enablePropertyConnection,
        enablePushData: channelManagerData?.enablePushData,
      };
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/create-room-rate-plan`,
      });

      const { response, data } = await post(requestBody);
      if (response.status === 200) {
        getAllRooms();
        setSnackBarMsg({
          open: true,
          msg: "Saved Successfully",
          color: successColor,
        });
      } else {
        setSnackBarMsg({
          open: true,
          msg: data?.messageToUser || "Something went wrong",
          color: errorColor,
        });
      }
    } catch (err) {
      console.log(err);
    }
  }, [
    authFetch,
    channelManagerData.discount,
    channelManagerData.enablePropertyConnection,
    channelManagerData.enablePushData,
    channelManagerData.roomId,
    channelManagerData.roomRateGroupCode,
    hotelId,
    propertyId,
    getAllRooms,
  ]);

  const handleSaveChannelmanager = useCallback(
    async (value) => {
      try {
        setformEdit(false);
        setIsLoading(true);
        const requestBody = {
          propertyId: propertyId,
          roomId: channelManagerData.roomId,
          roomRateGroupCode: channelManagerData?.roomRateGroupCode,
          discount: channelManagerData?.discount,
          enablePropertyConnection:
            value?.connectionStatus !== undefined
              ? value?.connectionStatus
              : channelManagerData?.enablePropertyConnection,
          enablePushData:
            value?.pushDataStatus !== undefined
              ? value?.pushDataStatus
              : channelManagerData?.pushDataStatus,
        };
        // const { put } = await authFetch({
        //   path: `/hotel/${hotelId}/update-room-rate-plan?ratePlanId=${channelManagerData?.RoomRatePlanId}`,
        // });
        const { post } = await authFetch({
          path: `/hotel/${hotelId}/create-room-rate-plan`,
        });

        const { response, data } = await post(requestBody);
        if (response.status === 200) {
          getAllRooms();
          setSnackBarMsg({
            open: true,
            msg: "Saved Successfully",
            color: successColor,
          });
        } else {
          setSnackBarMsg({
            open: true,
            msg: data?.messageToUser || "Something went wrong",
            color: errorColor,
          });
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      authFetch,
      channelManagerData.discount,
      channelManagerData.enablePropertyConnection,
      channelManagerData.pushDataStatus,
      channelManagerData.roomId,
      channelManagerData.roomRateGroupCode,
      getAllRooms,
      hotelId,
      propertyId,
      setformEdit,
    ]
  );

  const handleCreateRooms = useCallback(
    async (value, index, isResavenue) => {
      try {
        setformEdit(false);
        setLoaderRoomIndex(index);
        setIsLoading(true);
        const requestBody = getPayload(value, index, isResavenue);

        const { post } = await authFetch({
          path: `/hotel/${hotelId}/create-room-rate-plan`,
        });

        const { response } = await post(requestBody);
        if (response.status === 200) {
          getAllRooms();
          setSnackBarMsg({
            open: true,
            msg: response?.data?.type,
            color: successColor,
          });
        } else {
          setSnackBarMsg({
            open: true,
            msg: response?.data?.messageToUser,
            color: errorColor,
          });
        }
      } catch (err) {
        console.error(err);
        setSnackBarMsg({
          open: true,
          msg: "Something Went Wrong!",
          color: errorColor,
        });
      } finally {
        setIsLoading(false);
        setLoaderRoomIndex(null);
      }
    },
    [authFetch, getAllRooms, hotelId, setformEdit]
  );

  const handleUpdateRooms = useCallback(
    async (value, index, isResavenue) => {
      try {
        setformEdit(false);
        setLoaderRoomIndex(index);
        setIsLoading(true);
        const requestBody = getPayload(value, index, isResavenue);

        const { put } = await authFetch({
          path: `/hotel/${hotelId}/update-room-rate-plan?ratePlanId=${value?.RoomRatePlanId}`,
        });

        const { response } = await put(requestBody);
        if (response.status === 200) {
          getAllRooms();
          setSnackBarMsg({
            open: true,
            msg: response?.data?.type,
            color: successColor,
          });
        } else {
          setSnackBarMsg({
            open: true,
            msg: response?.data?.messageToUser,
            color: errorColor,
          });
        }
      } catch (err) {
        console.error(err);
        setSnackBarMsg({
          open: true,
          msg: "Something Went Wrong!",
          color: errorColor,
        });
      } finally {
        setIsLoading(false);
        setLoaderRoomIndex(null);
      }
    },
    [authFetch, getAllRooms, hotelId, setformEdit]
  );

  const handleSaveRooms = useCallback(
    (index, isChannelManagerTypeResavenue) => {
      roomsData?.[index]?.isNewRoom
        ? handleCreateRooms(
            roomsData?.[index],
            index,
            isChannelManagerTypeResavenue
          )
        : handleUpdateRooms(
            roomsData?.[index],
            index,
            isChannelManagerTypeResavenue
          );
    },
    [handleCreateRooms, handleUpdateRooms, roomsData]
  );

  const changeHandler = useCallback((index, key, value) => {
    setRoomsData((prevState) => {
      return prevState.map((data, idx) => {
        if (index === idx) {
          return {
            ...data,
            isEdit: true,
            [key]: value,
          };
        }
        return data;
      });
    });
  }, []);

  const handleCheckBoxChange = useCallback((key, value) => {
    setCurrentHotelData((prev) => ({
      ...prev,
      [key]: NON_BOOLEAN_CHECKBOX_FIELDS.includes(key) ? value : !prev[key],
    }));
    setIsCheckBoxEdited(true);
  }, []);

  const handleSaveCheckBox = useCallback(async () => {
    const {
      channelManagerAutoSyncEnabled,
      autoPushRatesToChannelManager,
      noOfDaysChannelManagerForPush,
      highDemandLevels,
    } = currentHotelData;
    const payload = {
      channelManagerAutoSyncEnabled,
      autoPushRatesToChannelManager,
      noOfDaysChannelManagerForPush: noOfDaysChannelManagerForPush || 0,
      highDemandLevels: highDemandLevels?.length > 0 ? highDemandLevels : null,
    };
    setIsLoading(true);
    try {
      const { put } = await authFetch({
        path: `/hotel/${hotelId}/channel-manager-properties`,
      });

      const { response, data } = await put(payload);
      if (response.ok) {
        setSnackBarMsg({
          open: true,
          msg: data?.message || "Saved Successfully",
          color: successColor,
        });
        getHotelDetails();
      } else {
        setSnackBarMsg({
          open: true,
          msg: data?.messageToUser || "Something went wrong",
          color: errorColor,
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
      setIsCheckBoxEdited(false);
    }
  }, [authFetch, currentHotelData, getHotelDetails, hotelId]);

  const handleClickConnectionButton = useCallback(async () => {
    try {
      const requestBody = {
        action: channelManagerData?.enablePropertyConnection
          ? "deactivate"
          : "activate",
      };
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/enable-disable-property-connection`,
      });
      const { response, data } = await post(requestBody);
      if (response.status === 200) {
        // handleSaveChannelmanager({
        //   pushDataStatus: channelManagerData.enablePushData,
        //   connectionStatus: !channelManagerData.enablePropertyConnection,
        // });
        setSnackBarMsg({
          open: true,
          msg: data?.messageToUser || "Enabled Successfully",
          color: "#34A853",
        });
      } else {
        setSnackBarMsg({
          open: true,
          msg: data?.messageToUser || "Something went wrong",
          color: "#CA3433",
        });
      }
    } catch (err) {
      console.log(err);
    }
  }, [authFetch, channelManagerData.enablePropertyConnection, hotelId]);

  const handlePushDataClick = useCallback(async () => {
    try {
      const requestBody = {
        action: channelManagerData?.enablePushData ? "deactivate" : "activate",
      };
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/enable-disable-push-data-api`,
      });
      const { response, data } = await post(requestBody);
      if (response.status === 200) {
        // handleSaveChannelmanager({
        //   pushDataStatus: !channelManagerData.enablePushData,
        //   connectionStatus: channelManagerData.enablePropertyConnection,
        // });
        setSnackBarMsg({
          open: true,
          msg: "Pushed Data Successfully",
          color: successColor,
        });
      } else {
        setSnackBarMsg({
          open: true,
          msg: data?.messageToUser || "Something went wrong",
          color: "#CA3433",
        });
      }
    } catch (err) {
      console.log(err);
    }
  }, [authFetch, channelManagerData.enablePushData, hotelId]);

  const fetchArrData = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/fetch-arr-staah`,
      });
      const { data, response } = await get();
      if (data && response.status === 200) {
        setArrData(data);
      }
    } catch (err) {
      console.log(err);
    }
  }, [authFetch, hotelId]);

  useEffect(() => {
    fetchArrData();
  }, [fetchArrData]);

  const handleSearchPropertyId = useCallback(async () => {
    await getRoomMapping();
    setOpenPropertyIdModal(true);
  }, [getRoomMapping]);

  const actionOnConnectionButtonClick = useCallback(() => {
    if (channelManagerData?.enablePropertyConnection) {
      setOpenWarningModal(true);
    } else {
      handleClickConnectionButton();
    }
  }, [
    channelManagerData.enablePropertyConnection,
    handleClickConnectionButton,
  ]);

  return {
    hotelId,
    handleRedirect,
    propertyId,
    currentHotel,
    setPropertyId,
    channelManagerData,
    setChannelManagerData,
    roomData,
    roomsData,
    setRoomsData,
    getRoomMapping,
    handleSavePropertyId,
    handleSaveChannelmanager,
    snackbarMsg,
    setSnackBarMsg,
    isLoading,
    handleClickConnectionButton,
    handlePushDataClick,
    arrData,
    openPropertyIdModal,
    setOpenPropertyIdModal,
    handleSearchPropertyId,
    openWarningModal,
    setOpenWarningModal,
    actionOnConnectionButtonClick,
    formEdit,
    setformEdit,
    loaderRoomIndex,
    changeHandler,
    handleSaveRooms,
    excludeRoomToggle,
    setExcludeRoomToggle,
    authFetch,
    checkBoxFields,
    isCheckBoxEdited,
    setIsCheckBoxEdited,
    handleCheckBoxChange,
    handleSaveCheckBox,
    currentHotelData,
    reorderRoomMappingApi,
    demandLevels,
  };
}
